import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { TEMPLE, TEMPLE_FONTS } from '../common/templeStyles';

/* ------------------------------------------------------------------ *
 * LoaderText — the spiritual boot loader for the admin app, matching
 * the guest home-page Preloader: a clay diya with a flickering flame
 * beneath a glowing "ॐ", framed by a slowly turning mandala ring and
 * drifting devanagari accents on a warm cream wash.
 *
 * The invocation beneath it rotates every 2 seconds. Everything is MUI
 * `sx` + keyframes over the shared temple tokens, and every animation
 * stands down under `prefers-reduced-motion`.
 * ------------------------------------------------------------------ */

const ROTATE_MS = 2000;

const flicker = keyframes`
  from { transform: scale(1, 1) rotate(-2deg); }
  to   { transform: scale(1.08, 0.94) rotate(2.5deg); }
`;

const omGlow = keyframes`
  from { text-shadow: 0 0 12px rgba(232, 114, 28, 0.3); transform: scale(1); }
  to   { text-shadow: 0 0 34px rgba(232, 114, 28, 0.7), 0 0 70px rgba(200, 155, 60, 0.4); transform: scale(1.06); }
`;

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const haloPulse = keyframes`
  0%, 100% { opacity: 0.45; transform: translate(-50%, -50%) scale(1); }
  50%      { opacity: 0.75; transform: translate(-50%, -50%) scale(1.15); }
`;

const floatOm = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); opacity: 0.35; }
  50%      { transform: translateY(-18px) rotate(5deg); opacity: 0.7; }
`;

const fadeSlide = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: none; }
`;

const reduceMotion = '@media (prefers-reduced-motion: reduce)';

/* ---------- Invocations, rotated every ROTATE_MS ---------- */

const CONTENT = [
  { title: 'Power of Intention', subtitle: 'We cannot change the truth. But the truth can change us.', hi: 'सेवा ही सत्य है' },
  { title: 'Learning', subtitle: 'A little faith will take you to Heaven.', hi: 'श्रद्धा ही पथ है' },
  { title: 'Learning & Earning', subtitle: 'Full faith will bring Heaven to you.', hi: 'कर्म ही पूजा है' },
  { title: 'Learning, Earning, Returning', subtitle: 'The journey from growth to giving.', hi: 'दान ही धर्म है' },
  { title: 'Three Life Stages', subtitle: 'Learn • Earn • Return', hi: 'सीखो • कमाओ • लौटाओ' },
  { title: 'The Truth', subtitle: 'Timeless. Transformational. Eternal.', hi: 'सत्यमेव जयते' },
];

/* Devanagari/temple accents drifting behind the lamp. Deliberately no
   swastika: the sacred Hindu glyph is widely misread in this form. */
type Accent = {
  char: string;
  size: number;
  delay: string;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
};

const ACCENTS: Accent[] = [
  { char: 'ॐ', top: '18%', left: '18%', size: 34, delay: '0s' },
  { char: 'ॐ', top: '26%', right: '16%', size: 24, delay: '1.2s' },
  { char: 'ॐ', bottom: '22%', left: '22%', size: 24, delay: '2.1s' },
  { char: 'ॐ', bottom: '26%', right: '20%', size: 20, delay: '0.6s' },
];

const MandalaRing = () => (
  <Box
    component="svg"
    viewBox="0 0 200 200"
    aria-hidden="true"
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 210,
      height: 210,
      marginTop: '-105px',
      marginLeft: '-105px',
      opacity: 0.22,
      animation: `${ringSpin} 26s linear infinite`,
      [reduceMotion]: { animation: 'none' },
    }}
  >
    <g fill="none" stroke={TEMPLE.gold} strokeWidth="0.8">
      <circle cx="100" cy="100" r="96" />
      <circle cx="100" cy="100" r="80" strokeDasharray="3 6" />
      <g id="loader-petal">
        <path d="M100 6 C 111 32, 111 56, 100 70 C 89 56, 89 32, 100 6 Z" />
      </g>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <use key={deg} href="#loader-petal" transform={`rotate(${deg} 100 100)`} />
      ))}
    </g>
  </Box>
);

/* Clay oil lamp: bowl, wick, flame and the warm glow it throws. */
const Diya = () => (
  <Box sx={{ position: 'relative', width: 140, height: 76, mt: 1.5 }}>
    {/* Warm glow cast by the flame */}
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: 6,
        left: '50%',
        width: 120,
        height: 120,
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(245, 166, 35, 0.35) 0%, transparent 65%)',
        animation: `${haloPulse} 2.6s ease-in-out infinite`,
        [reduceMotion]: { animation: 'none' },
      }}
    />

    {/* Flame */}
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: -14,
        left: '50%',
        ml: '-7px',
        width: 14,
        height: 24,
        borderRadius: '50% 50% 50% 50% / 62% 62% 38% 38%',
        background: 'radial-gradient(ellipse at bottom, #ffd98a 0%, #f5a623 45%, #e8721c 80%, transparent)',
        filter: 'blur(0.4px)',
        boxShadow: '0 0 18px rgba(245, 166, 35, 0.8), 0 0 44px rgba(232, 114, 28, 0.4)',
        transformOrigin: 'bottom center',
        animation: `${flicker} 0.28s ease-in-out infinite alternate`,
        [reduceMotion]: { animation: 'none' },
      }}
    />

    {/* Lamp body */}
    <Box component="svg" viewBox="0 0 140 76" aria-hidden="true" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="loader-clay" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C97A34" />
          <stop offset="55%" stopColor="#A2551C" />
          <stop offset="100%" stopColor="#6E340E" />
        </linearGradient>
        <linearGradient id="loader-ghee" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE3A8" />
          <stop offset="100%" stopColor="#F0B24A" />
        </linearGradient>
      </defs>
      {/* wick */}
      <path d="M70 14 L70 26" stroke="#5E3A18" strokeWidth="3" strokeLinecap="round" />
      {/* ghee pool */}
      <ellipse cx="70" cy="27" rx="41" ry="7" fill="url(#loader-ghee)" />
      {/* bowl */}
      <path d="M29 27 C 33 62, 107 62, 111 27 Z" fill="url(#loader-clay)" />
      {/* rim highlight */}
      <ellipse cx="70" cy="27" rx="41" ry="7" fill="none" stroke="#E0A15E" strokeWidth="1.4" />
      {/* base */}
      <ellipse cx="70" cy="62" rx="20" ry="5" fill="#5E2C0C" opacity="0.55" />
    </Box>
  </Box>
);

const LoaderText: React.FC = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % CONTENT.length);
    }, ROTATE_MS);
    return () => clearInterval(interval);
  }, []);

  const item = CONTENT[index];

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Loading"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: `linear-gradient(160deg, ${TEMPLE.cream}, #F5E8CE)`,
      }}
    >
      {/* Drifting devanagari accents */}
      {ACCENTS.map((a) => (
        <Box
          key={`${a.char}-${a.delay}`}
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: a.top,
            left: a.left,
            right: a.right,
            bottom: a.bottom,
            fontFamily: TEMPLE_FONTS.dev,
            fontSize: a.size,
            color: TEMPLE.gold,
            opacity: 0.35,
            userSelect: 'none',
            animation: `${floatOm} 7s ease-in-out infinite`,
            animationDelay: a.delay,
            [reduceMotion]: { animation: 'none' },
          }}
        >
          {a.char}
        </Box>
      ))}

      {/* Mandala + ॐ */}
      <Box sx={{ position: 'relative', width: 210, height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MandalaRing />
        <Box
          aria-hidden="true"
          sx={{
            fontFamily: TEMPLE_FONTS.dev,
            fontSize: 78,
            lineHeight: 1,
            color: TEMPLE.saffron,
            animation: `${omGlow} 2s ease-in-out infinite alternate`,
            [reduceMotion]: { animation: 'none' },
          }}
        >
          ॐ
        </Box>
      </Box>

      <Diya />

      {/* Rotating invocation */}
      <Typography
        key={item.title}
        variant="h5"
        sx={{
          mt: 3,
          px: 2,
          textAlign: 'center',
          fontFamily: TEMPLE_FONTS.display,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: TEMPLE.maroon,
          animation: `${fadeSlide} .8s ease`,
          [reduceMotion]: { animation: 'none' },
        }}
      >
        {item.title}
      </Typography>

      <Typography
        key={item.subtitle}
        variant="body2"
        sx={{
          mt: 1.25,
          px: 2,
          maxWidth: 420,
          textAlign: 'center',
          fontStyle: 'italic',
          color: TEMPLE.inkM,
          animation: `${fadeSlide} 1s ease`,
          [reduceMotion]: { animation: 'none' },
        }}
      >
        {item.subtitle}
      </Typography>

      <Typography
        key={item.hi}
        aria-hidden="true"
        sx={{
          mt: 1.5,
          fontFamily: TEMPLE_FONTS.dev,
          fontSize: 13,
          letterSpacing: '2px',
          color: TEMPLE.goldD,
          animation: `${fadeSlide} 1.2s ease`,
          [reduceMotion]: { animation: 'none' },
        }}
      >
        {item.hi}
      </Typography>
    </Box>
  );
};

export default LoaderText;
